import { useState, useCallback, useEffect } from 'react';
import { STORAGE_KEYS } from './src-config-miaw-constants.js';
import { getContinuityJwt, listConversations, listConversationEntries } from './src-utils-miaw-api-utils.js';
import { setJwt, storeConversationId } from './src-utils-miaw-chat-config.js';
import { setItemInWebStorage } from './src-utils-miaw-web-storage-utils.js';
import { getConversationEventMessage } from './src-utils-component-utils.js';
import { useNRLogging } from './src-hooks-custom-hooks-useNRLogging.js';
import { useMIAWAgentActions } from './src-hooks-miaw-useMIAWAgentActions.js';
import { handleLink } from './src-utils-miaw-agent-messages-utils.js';
import { setIsUserLoggedInAtChatStart } from './src-utils-session-storage-utils.js';
import { useIdentity } from '@vp/auth-react';

const useMIAWChatRetrieve = () => {
  const identity = useIdentity();
  const { logCustomAttributesNR } = useNRLogging();
  const { subscribeToAgentActions } = useMIAWAgentActions();
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [toggle, setToggle] = useState(false);
  const trigger = useCallback(() => {
    setToggle((prev) => !prev);
  }, []);
  useEffect(() => {
    if (toggle) {
      setLoading(true);
      try {
        logCustomAttributesNR("ca-chatbot-miaw-api-request", {
          logLevel: "info",
          message: "Retrieving conversation",
          apiType: "miawRetrieveConversation"
        });
        getContinuityJwt().then((response) => {
          setJwt(response.accessToken);
          setItemInWebStorage(STORAGE_KEYS.JWT, response.accessToken);
        }).then(() => {
          listConversations().then((response) => {
            if (response && response.openConversationsFound > 0 && response.conversations.length) {
              const openConversations = response.conversations;
              if (openConversations.length > 1) {
                console.warn(
                  `Expected the user to be participating in 1 open conversation but instead found ${openConversations.length}. Loading the conversation with latest startTimestamp.`
                );
                openConversations.sort((conversationA, conversationB) => conversationB.startTimestamp - conversationA.startTimestamp);
              }
              storeConversationId(openConversations[0].conversationId);
              listConversationEntries(openConversations[0].conversationId).then((response2) => {
                const conversationEntries = [];
                let agentName = "";
                let parsedPayload, messageContent;
                response2.reverse().forEach((item) => {
                  const entry = item.conversationEntry;
                  switch (entry.entryType) {
                    case "Message": {
                      parsedPayload = entry.entryPayload;
                      let urlMapping = void 0;
                      if (parsedPayload.abstractMessage.staticContent.formatType === "RichLink") {
                        const linkData = handleLink(parsedPayload);
                        if (linkData) urlMapping = linkData;
                      }
                      const messageList = [];
                      if (parsedPayload.abstractMessage.staticContent.text) {
                        messageList.push(parsedPayload.abstractMessage.staticContent.text);
                      }
                      if (urlMapping?.url) {
                        messageList.push(urlMapping.url);
                      }
                      conversationEntries.push({
                        id: entry.identifier,
                        messageContent: messageList.length > 0 ? messageList : [""],
                        senderDisplayName: entry.senderDisplayName,
                        timestamp: entry.clientTimestamp,
                        type: entry.sender.role === "EndUser" ? "user" : "agent",
                        showFeedback: false,
                        formatType: parsedPayload?.abstractMessage?.staticContent?.formatType,
                        attachments: parsedPayload?.abstractMessage?.staticContent?.formatType === "Attachments" ? parsedPayload?.abstractMessage?.staticContent?.attachments : void 0,
                        urlMapping: [...urlMapping ? [urlMapping] : []]
                      });
                      break;
                    }
                    case "RoutingResult":
                      parsedPayload = entry.entryPayload;
                      conversationEntries.push({
                        id: entry.identifier,
                        messageContent: "A specialist will be with you soon",
                        senderDisplayName: entry.senderDisplayName,
                        timestamp: entry.clientTimestamp,
                        type: "system",
                        showFeedback: false,
                        formatType: parsedPayload?.abstractMessage?.staticContent?.formatType,
                        attachments: parsedPayload?.abstractMessage?.staticContent?.formatType === "Attachments" ? parsedPayload?.abstractMessage?.staticContent?.attachments : void 0
                      });
                      break;
                    case "ParticipantChanged":
                      parsedPayload = entry.entryPayload;
                      messageContent = getConversationEventMessage(parsedPayload.entries[0].operation, parsedPayload.entries[0].displayName);
                      conversationEntries.push({
                        id: entry.identifier,
                        messageContent,
                        senderDisplayName: entry.senderDisplayName,
                        timestamp: entry.clientTimestamp,
                        type: "system",
                        showFeedback: false,
                        formatType: parsedPayload?.abstractMessage?.staticContent?.formatType,
                        attachments: parsedPayload?.abstractMessage?.staticContent?.formatType === "Attachments" ? parsedPayload?.abstractMessage?.staticContent?.attachments : void 0
                      });
                      if (parsedPayload.entries[0].operation === "add") agentName = parsedPayload.entries[0].displayName;
                      break;
                    default:
                      (void 0);
                  }
                });
                const { isSignedIn } = identity;
                setIsUserLoggedInAtChatStart(isSignedIn);
                setData({ messages: conversationEntries, conversationId: openConversations[0].conversationId, agentName });
                setError(void 0);
                subscribeToAgentActions();
                logCustomAttributesNR("ca-chatbot-miaw-api-response", {
                  logLevel: "info",
                  message: "Conversation retrieved successfully",
                  conversationId: openConversations[0].conversationId,
                  apiType: "miawRetrieveConversation"
                });
              });
            } else {
            }
          }).catch((error2) => {
            setError(error2);
            console.error("Error in retrieving conversation", error2);
            logCustomAttributesNR("ca-chatbot-miaw-api-error", {
              errorName: error2.name,
              errorStack: error2.stack,
              message: error2.message,
              logLevel: "error",
              apiType: "miawRetrieveConversation"
            });
          });
        }).catch((error2) => {
          setError(error2);
          console.error("Error in retrieving conversation", error2);
          logCustomAttributesNR("ca-chatbot-miaw-api-error", {
            errorName: error2.name,
            errorStack: error2.stack,
            message: error2.message,
            logLevel: "error",
            apiType: "miawRetrieveConversation"
          });
        }).finally(() => {
          setLoading(false);
        });
      } catch (error2) {
        console.error("Error in retrieving conversation", error2);
        setError(error2);
        logCustomAttributesNR("ca-chatbot-miaw-api-error", {
          errorName: error2.name,
          errorStack: error2.stack,
          message: error2.message,
          logLevel: "error",
          apiType: "miawRetrieveConversation"
        });
      } finally {
        setToggle(false);
        setLoading(false);
      }
    }
  }, [toggle]);
  return {
    loading,
    trigger,
    data,
    error
  };
};

export { useMIAWChatRetrieve };
//# sourceMappingURL=src-hooks-miaw-useMIAWChatRetrieve.js.map
